import * as React from 'react';
import { useEffect, useState } from 'react';

import { FavouriteResultComponentProps } from 'app/area/favourite/types';
import { useCaseTableViewEvents, useSelectors } from 'app/area/case-report/case-table-view/hooks/case-table-view-hooks';
import { useSyncScroll } from 'app/area/case-report/case-table-view/hooks/sync-scroll-hooks';

import { CaseTableViewHeader } from 'app/area/case-report/case-table-view/components/case-table-view-header';
import { CaseTableViewRows } from 'app/area/case-report/case-table-view/components/case-table-view-rows';
import { ElementLoader } from 'ui';
import { Waypoint } from 'react-waypoint';
import { CaseTableMobileView } from 'app/area/case-report/case-table-view/components/case-table-mobile-view';
import { MOBILE_BREAKPOINT, caseTableViewTranslation } from 'app/area/case-report/case-table-view/constants';

import { useCaseListingException } from 'app/area/case-report/components/use-case-listing-exception';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { STATE } from 'app/constants';
import { caseListingTranslation } from 'app/area/case-report/case-listing/constants';

import styles from './case-table-view.scss';
import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { CaseListingInfo } from 'app/area/case-report/components/case-listing-no-columns-selected';

interface CaseTableViewContainerProps extends FavouriteResultComponentProps {
  autoExecute: boolean;
}

export type TranslationType = {
  readonly [P in keyof typeof caseListingTranslation]: string
} & {
  readonly [P in keyof typeof caseTableViewTranslation]: string
};
export const CaseTableViewContainer = (props: CaseTableViewContainerProps) => {
  const translation = useTranslation(STATE.caseListing, { ...caseListingTranslation, ...caseTableViewTranslation });
  const [resultsLoaded, setResultsLoaded] = useState<boolean>(false);
  const { autoExecute, workingDefinition } = props;
  const { execute, windowWidth, onDeleteCase, onCopyRiskEvaluation } = useCaseTableViewEvents(workingDefinition, autoExecute);
  const { totalNumberOfResults, entities, columns, fetching, locale, errorsExecuteFailed } = useSelectors();
  const tableViewRef = React.useRef<HTMLDivElement>(null);
  const headerContainerRef = React.useRef<HTMLDivElement>(null);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const { handleScroll, tableWidth, tableColumnWidths } = useSyncScroll({ tableContainerRef, resultsLoaded });
  const canFetchMore = !!entities?.length && (entities?.length < totalNumberOfResults);

  const {
    height: maxHeight
  } = useInstanceOptions();

  useEffect(() => {
    setResultsLoaded(!fetching);
  }, [fetching]);

  const fetchMoreData = () => {
    if (autoExecute && !fetching && columns?.length) {
      execute(entities?.length);
    }
  };

  const ErrorHandlingComponent = useCaseListingException({
    autoExecute,
    errorsExecuteFailed,
    fetching,
    totalNumberOfResults,
    translation
  });

  if (ErrorHandlingComponent !== null) {
    return ErrorHandlingComponent;
  }

  if(!columns?.length && !!totalNumberOfResults) {
    return <CaseListingInfo message={translation.noColumnsWereSelected} />;
  }
  const isTableView = (windowWidth > MOBILE_BREAKPOINT) && columns?.length;
  const isCardView = !(windowWidth > MOBILE_BREAKPOINT) && columns?.length;
  return (<div className={styles.caseViewTableWrapper} ref={tableViewRef}>
    {totalNumberOfResults ? <div className={`${styles.caseViewTableContainer} scrollable`} style={maxHeight ? { maxHeight } : null}>
      {isTableView ? (
        <div className={styles.tableView} >
          <CaseTableViewHeader
            columns={columns}
            tableWidth={tableWidth}
            columnWidths={tableColumnWidths}
            containerRef={headerContainerRef}
            handleScroll={handleScroll}
          />
          <CaseTableViewRows
            rows={entities}
            columns={columns}
            locale={locale}
            containerRef={tableContainerRef}
            translation={translation}
            onDeleteCase={onDeleteCase}
            onCopyRiskEvaluation={onCopyRiskEvaluation}
            tableViewRef={tableViewRef}
          />
        </div>
      ): ''}

      {isCardView ? (
        <div className={`${styles.cardView} card-view`}>
          <CaseTableMobileView rows={entities} columns={columns} locale={locale}
            translation={translation}
            onDeleteCase={onDeleteCase}
            onCopyRiskEvaluation={onCopyRiskEvaluation}
            tableViewRef={tableViewRef}
          />
        </div>
      ): ''}

      {canFetchMore && <Waypoint key={`wp-${entities?.length || 0}`}
        onEnter={fetchMoreData}
        bottomOffset="-10%" />}
      {fetching && <ElementLoader />}
    </div> :
      null
    }
  </div>);
};
